import type { CopyShape } from "@tami/core/intl";

import type { HOME_COPY_PT_BR } from "../home.copy";

export const HOME_COPY_ES_ES: CopyShape<typeof HOME_COPY_PT_BR> = {
  taglines: {
    connect: {
      before: "Conecta todas las piezas de tu vida de Creator en",
      italic: "un solo lugar.",
    },
    transformLine1: {
      before: "Convierte el desorden en",
      italic: "productividad.",
    },
    transformLine2: {
      before: "Y la incertidumbre con la pasta en",
      italic: "control total.",
    },
    closing: {
      before: "Tú creas.",
      italic: "Tãmi se encarga del resto.",
    },
    testimonials: {
      before: "No lo decimos nosotros.",
      italic: "Lo dicen ellos.",
    },
  },

  hero: {
    stickersAlt: "Stickers decorativos",
    headingLead: "Tu carrera de Creator,",
    headingHighlights: ["Organizada.", "Profesional.", "Rentable."],
    description:
      "Tãmi es la herramienta para que seas el Creator que siempre has querido ser: eficiente, rentable y con tiempo para lo que de verdad importa.",
    photoAlt: "mujeres jóvenes vestidas con moda de los años 2000",
    scrollDownAria: "Desplazarse hacia abajo",
    events: [
      { title: "Grabación exterior - Publi X", timeRange: "16:30 - 17:30" },
      { title: "Grabar contenido orgánico", timeRange: "19:00 - 22:00" },
      { title: "Cita con la logopeda", timeRange: "10:00 - 12:00" },
      { title: "Llamada de alineación", timeRange: "08:30 - 09:30" },
    ],
  },

  navigationButtons: {
    content: "Contenido",
    finances: "Finanzas",
    agenda: "Calendario",
    team: "Equipo",
    createAccount: "Crea tu cuenta",
    iconAlt: "Icono de Tami",
  },

  features: {
    heading: {
      before: "¿No sabes por",
      where: "dónde",
      start: "empezar",
      afterStart: "?",
      followSteps: "sigue los pasos de",
      brand: "Tãmi",
      pro: "Pro",
    },
    cta: "Quiero mi acceso ya",
    steps: [
      {
        title: "Tormenta creativa",
        description:
          "Donde todo empieza: una idea, un arrebato, una chispa en mitad de la nada. ¿El truco? Apuntarla antes de que se esfume.",
      },
      {
        title: "Evaluación intuitiva",
        description:
          "Toca pensar: ¿esta idea tiene sentido de verdad? ¿Encaja conmigo, con mi momento, con quien me sigue?",
      },
      {
        title: "Planificación estratégica",
        description: "¿La idea ha pasado el filtro? Ahora necesita formato, datos, objetivo y plan de acción.",
      },
      {
        title: "Puesta en marcha",
        description: "¡Manos a la obra! Es el momento de crear, grabar, revisar o seguir de cerca a quien lo ejecuta.",
      },
      {
        title: "Replay creativo",
        description:
          "El contenido ya está ahí fuera. ¿Y ahora qué? Analizar, entender qué ha funcionado y usarlo como gasolina para la próxima idea.",
      },
    ],
  },

  featuresSession: {
    cards: [
      { title: "Finanzas", subtitle: "del creator" },
      { title: "Calculadora", subtitle: "de publis" },
      { title: "Calendario", subtitle: "del creator" },
    ],
    imageAlt: "Grupo de móviles mostrando funciones de Tãmi",
  },

  bannerImage: {
    imageAlt: "Persona trabajando con un iPad que muestra el calendario del creator en Tãmi",
    glassCard: {
      titleLine1: "Olvídate de las hojas de cálculo",
      titleLine2: "y las notas sueltas.",
      paragraphLine1: "Centraliza tu vida de creator y",
      paragraphLine2: "céntrate en lo que de verdad importa.",
      paragraphBold: "Crear.",
      cta: "Crea tu cuenta",
      iconAlt: "Icono de Tami",
    },
  },

  bannerPhone: {
    title: "No vuelvas a perder una idea ni un plazo.",
    imageAlt: "App de Tami en un iPhone con fondo degradado",
    text: "Con Tãmi tienes una asistente inteligente que te acompaña a todas partes.",
    cta: "Crea tu cuenta",
  },

  bannerImgBody: {
    imageAlt: "Promotion Footer",
    logoAlt: "Logo de Tãmi",
    taglineLine1: "Una solución completa, hecha en Brasil,",
    taglineLine2: "para impulsar a los Creators.",
  },

  heroScrollDemo: {
    rainbowCardAlt: "Rainbow card",
    tags: {
      content: "Contenido",
      finances: "Finanzas",
      agenda: "Calendario",
      team: "Equipo",
    },
    createAccount: "Crea tu cuenta",
    iconAlt: "Icono de Tami",
    desktopSlides: [
      "Dashboard - Panel principal",
      "Dashboard - Vista detallada",
      "Rendimiento del creator - Página 1",
      "Rendimiento del creator - Página 2",
      "Rendimiento del creator - Página 3",
      "Calculadora de publis - Página 1",
      "Calculadora de publis - Página 2",
      "Finanzas del creator - Página 1",
      "Finanzas del creator - Página 2",
      "Finanzas del creator - Página 3",
      "Centro de campañas",
      "Tareas y recordatorios",
      "Notas del creator",
      "Calendario",
      "Flujo de la semana",
    ],
    mobileSlides: [
      "Dashboard - Panel principal",
      "Dashboard - Vista detallada",
      "Dashboard - Análisis avanzado",
      "Dashboard - Informes",
      "Rendimiento del creator - Página 1",
      "Rendimiento del creator - Página 2",
      "Rendimiento del creator - Página 3",
      "Rendimiento del creator - Página 4",
      "Calculadora de publis - Página 1",
      "Calculadora de publis - Página 2",
      "Calculadora de publis - Página 3",
      "Finanzas del creator - Página 1",
      "Finanzas del creator - Página 2",
      "Finanzas del creator - Página 3",
      "Finanzas del creator - Página 4",
      "Centro de campañas",
      "Tareas y recordatorios",
      "Notas del creator",
      "Calendario",
      "Flujo de la semana",
    ],
  },

  infoMessage: {
    titleBold1: "¿Harto",
    titleDe: "de",
    titleTools: "herramientas",
    titleThatDont: "que no te",
    titleBold2: "entienden",
    titleQuestion: "?",
    bodyBefore: "Mientras otras plataformas intentaban",
    bodyItalic: "meter al creator",
    bodyAfter:
      "en un modelo de empresa rígido, Tãmi nació de nuestro caos real, de la rutina a tope, de los plazos ajustados y de la cabeza llena de quien vive de crear.",
    tagline: "Ha llegado para poner orden en el caos de quien está a tope con esto.",
    cta: "Quiero organizarme con Tãmi",
  },

  blackFriday: {
    label: "¡TÃMI SE APUNTA AL BLACK FRIDAY!",
    iconAlt: "Tãmi",
  },

  testimonials: {
    sectionAria: "Testimonios de creators",
    memberSince: "en Tãmi desde",
  },

  pricing: {
    title: {
      line1: "Nuestros precios, sin rodeos,",
      line2: "sin cargos extra.",
    },
    subtitle: "Tãmi es para creators en distintos momentos de su carrera.",
    audienceAria: "Elige el tipo de cuenta",
    audienceTabs: {
      creator: "Soy Creator",
      agency: "Soy Collab",
    },
    cycleAnnualAria: "Facturación anual",
    monthly: "Mensual",
    annual: "Anual",
    bridge: {
      toCreator: "¿Creas contenido por tu cuenta? Los planes individuales están en «Soy Creator».",
      toCollab: {
        before: "¿Gestionas creators? Echa un vistazo a",
        bold: "Tãmi Enterprise",
        after: "en «Soy Collab».",
      },
    },
  },

  planCard: {
    mostChosen: "El más elegido",
    onRequestDescription: "Nuestro equipo prepara contigo la propuesta adecuada.",
    free: "Gratis",
    freeForever: "Para siempre, con los límites de abajo.",
    perYear: "/año",
    perMonth: "/mes",
    includedFree: "Qué incluye",
    includedPaid: "Qué te llevas",
  },

  benefits: {
    title: "¿Es para ti?",
    items: {
      balance: {
        title: "Más equilibrio entre vida y trabajo",
        description: "Facilita la gestión del tiempo y te da más equilibrio entre la vida personal y la profesional.",
      },
      empowerment: {
        title: "Empoderamiento profesional",
        description: "Te da herramientas para una gestión más estratégica y para crecer en tu carrera.",
      },
      stress: {
        title: "Menos estrés",
        description: "Reduce la carga administrativa y el estrés de gestionar varios proyectos a la vez.",
      },
      clarity: {
        title: "Más claridad y enfoque",
        description: "Te ayuda a definir y alcanzar metas claras, con foco y dirección.",
      },
      relationships: {
        title: "Relaciones comerciales más sólidas",
        description: "Mejora la gestión de colaboraciones y negociaciones, y refuerza tus relaciones comerciales.",
      },
      growth: {
        title: "Crecimiento personal y profesional",
        description: "Contribuye a tu desarrollo continuo al optimizar la gestión de tus actividades.",
      },
    },
  },

  planComparison: {
    title: "Compara línea por línea",
    subtitle: "Las cifras salen de los límites configurados en cada plan.",
    caption: "Comparación de funciones y límites entre los planes de Tãmi",
    featureColumn: "Función",
  },

  priceOnRequest: {
    title: "Habla con nosotros",
  },

  featureList: {
    seeLess: "Ver menos",
    seeMore: (count: number): string => `Ver ${count} ${count === 1 ? "elemento" : "elementos"} más`,
  },

  enterpriseCard: {
    seatsPreview: "tú + 1 creator, y crece cuando lo necesites",
    onRequestDescription:
      "Paquete de la Collab más un importe por creator. Nuestro equipo prepara contigo la propuesta adecuada.",
    packageLabel: "Paquete de la Collab",
    perYear: "/año",
    perMonth: "/mes",
    perCreator: (formattedValue: string): string => `+ ${formattedValue} por creator`,
    badge: "Para Collabs",
    logoAlt: "Tami Logo",
    intro:
      "Para Collabs que gestionan creators y sus campañas. Un único panel para toda la Collab, y cada creator de la Collab usa Tãmi Pro completo, sin suscribirse aparte.",
    ctaTalk: "Hablar de Collab",
    bottomCta: {
      before: "Quiero hablar sobre",
      bold: "Tãmi para Collabs",
    },
    bottomCtaSignUp: {
      before: "¡Quiero Tãmi para mi",
      bold: "Collab!",
    },
    features: [
      {
        title: "Creators de la Collab",
        description: "Registra y organiza a todos los creators de la Collab en un solo panel, con una vista consolidada.",
      },
      {
        title: "Vinculación de creators",
        description: "Invita a quien ya usa Tãmi o crea cuentas nuevas para la Collab, con acceso controlado.",
      },
      {
        title: "Campañas de la Collab",
        description: "Sigue las campañas de cada creator y el estado de las entregas en un solo lugar.",
      },
      {
        title: "Propuestas de la Collab",
        description: "Prepara propuestas comerciales sumando el alcance de varios creators y expórtalas en PDF.",
      },
      {
        title: "Suscripción por plaza",
        description: "Una sola suscripción para la Collab, con plazas que crecen al ritmo de la Collab.",
      },
      {
        title: "Todo lo de Tãmi Pro",
        description: "Cada creator de la Collab usa todas las herramientas del plan Pro, sin suscribirse aparte.",
      },
    ],
  },
};
